import * as React from 'react';

import type {ImportIdentifier} from 'nutshell-graphql-types';

import {addNotification} from '../../../../../notifications/index';
import {useGetSessionUser} from '../../../../core/use-get-session-user';

import {DromoImportButton} from '../dromo-import-button';
import {useUploadDromoImportResults} from '../graphql/hooks/use-upload-dromo-import-results';

import {DROMO_QUOTA_FIELDS} from './consts';
import {useGetQuotaTemplate} from './graphql/hooks/use-get-quota-template';

type Props = {
    onUploaded: () => void;
    variant: 'primary' | 'secondary';
};

export function DromoQuotaImportButton(props: Props) {
    const {onUploaded} = props;
    const {uploadDromoImportResults} = useUploadDromoImportResults();
    const {user, isLoading: isUserLoading, errorMessage: userErrorMessage} = useGetSessionUser();

    const {quotasTemplate, isLoading: isTemplateLoading} = useGetQuotaTemplate();

    const fields = DROMO_QUOTA_FIELDS;

    const handleResults = React.useCallback(
        (
            response: unknown,
            metadata: {id: string; filename: string; importIdentifier: ImportIdentifier}
        ) => {
            uploadDromoImportResults({
                importData: JSON.stringify(response),
                dromoId: metadata.id,
                filename: metadata.filename,
                importIdentifier: metadata.importIdentifier,
            })
                .then(() => {
                    onUploaded();
                })
                .catch(() => {
                    addNotification('Something went wrong', {
                        icon: 'frowny',
                        body: 'We could not start the quota import. Please try again.',
                    });
                });
        },
        [onUploaded, uploadDromoImportResults]
    );

    return (
        <DromoImportButton
            fields={fields}
            columnHooks={[]}
            bulkRowHooks={[]}
            importIdentifier='QUOTA'
            importerTitle='Import quotas'
            onImportComplete={handleResults}
            user={user}
            isLoading={isUserLoading || isTemplateLoading}
            error={userErrorMessage}
            variant={props.variant}
            templateDownloadOverrideUrl={
                quotasTemplate?.csvContent
                    ? `data:text/csv;charset=utf-8,${encodeURIComponent(quotasTemplate.csvContent)}`
                    : undefined
            }
            styleOverrides={{
                global: {
                    primaryTextColor: '#211712',
                    secondaryTextColor: '#7a7370',
                    backgroundColor: '#f7f7f7',
                    borderRadius: '4px',
                },
                primaryButton: {
                    borderRadius: '3px',
                    backgroundColor: '#3b9eD7',
                    textColor: '#ffffff',
                    hoverBackgroundColor: '#337da8',
                    hoverTextColor: '#ffffff',
                },
                secondaryButton: {
                    borderRadius: '3px',
                    backgroundColor: '#ebe8e8',
                    textColor: '#4d4540',
                    hoverBackgroundColor: '#dbd9d9',
                    hoverTextColor: '#211712',
                },
                dropzone: {
                    borderColor: '#3b9eD7',
                    borderRadius: 4,
                },
                helpText: {
                    textColor: '#337da8',
                    backgroundColor: '#d9edf7',
                },
                stepperBar: {
                    currentColor: '#3b9eD7',
                    incompleteColor: '#7a7370',
                },
            }}
        />
    );
}
